<template>
  <div :class="['c-products-search-filters', containerClass]">
    <div class="c-products-search-filters__keyword-input-field">
      <i
        class="c-products-search-filters__keyword-input-search icon-search"
      ></i>
      <input
        type="text"
        v-model="searchParameters.keyword"
        placeholder="例）ハンディファン"
        @keydown.enter.prevent="onKeywordEnter"
        @compositionstart="isComposingKeyword = true"
        @compositionend="isComposingKeyword = false"
      />
      <i
        class="c-products-search-filters__keyword-input-clear icon-x"
        role="button"
        tabindex="0"
        aria-label="キーワードをクリア"
        @click="resetKeyword"
        @keydown.enter.prevent="resetKeyword"
        @keydown.space.prevent="resetKeyword"
      ></i>
    </div>

    <div class="c-products-search-filters__base-filters">
      <label class="c-products-search-filters__checkbox-wrapper">
        <input type="checkbox" v-model="searchParameters.onlyInStock" />
        <p class="c-products-search-filters__filter-option-label">在庫あり</p>
      </label>

      <label
        class="c-products-search-filters__checkbox-wrapper"
        v-if="hasSaleProductTypes"
      >
        <input type="checkbox" v-model="isSaleProductTypesChecked" />
        <p class="c-products-search-filters__filter-option-label">セール価格</p>
      </label>
    </div>

    <hr />

    <div class="c-products-search-filters__price-range-inputs">
      <div class="c-products-search-filters__price-range-input">
        <label class="c-products-search-filters__price-range-label"
          >最小価格</label
        >
        <div class="c-products-search-filters__price-range-input-field">
          <div class="c-products-search-filters__price-range-input-currency">
            ¥
          </div>
          <input
            type="number"
            v-model.number="searchParameters.priceRange.min"
            :min="aggregations.priceRange.min"
            :max="aggregations.priceRange.max"
            :placeholder="aggregations.priceRange.min"
          />
        </div>
      </div>

      <div class="c-products-search-filters__price-range-separator">〜</div>

      <div class="c-products-search-filters__price-range-input">
        <label class="c-products-search-filters__price-range-label"
          >最大価格</label
        >
        <div class="c-products-search-filters__price-range-input-field">
          <div class="c-products-search-filters__price-range-input-currency">
            ¥
          </div>
          <input
            type="number"
            v-model.number="searchParameters.priceRange.max"
            :min="aggregations.priceRange.min"
            :max="aggregations.priceRange.max"
            :placeholder="aggregations.priceRange.max"
          />
        </div>
      </div>
    </div>

    <hr />

    <div class="c-products-search-filters__sections">
      <section
        :class="[
          'c-products-search-filters__section',
          {
            'c-products-search-filters__section--active':
              openSection === 'category',
          },
        ]"
      >
        <button
          type="button"
          class="c-products-search-filters__section-header"
          @click="toggleSection('category')"
        >
          <h3 class="c-products-search-filters__filter-title">カテゴリ</h3>
          <p>
            <span
              v-if="selectedFilterCounts.category > 0"
              class="c-products-search-filters__filter-option-selected-count"
            >
              {{ selectedFilterCounts.category }}件選択中</span
            >

            <span class="c-products-search-filters__section-icon">{{
              openSection === 'category' ? '−' : '+'
            }}</span>
          </p>
        </button>
        <transition
          name="accordion"
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div
            v-show="openSection === 'category'"
            class="c-products-search-filters__section-body"
          >
            <label
              class="c-products-search-filters__checkbox-wrapper color-on-checked"
              v-for="category in aggregations.facetTags || []"
              :key="category.value"
            >
              <input
                type="checkbox"
                v-model="searchParameters.tagsSet.facetTags"
                :value="category.value"
              />
              <p
                class="c-products-search-filters__filter-option-label"
                v-text="category.label"
              ></p>
            </label>
          </div>
        </transition>
      </section>

      <section
        :class="[
          'c-products-search-filters__section',
          {
            'c-products-search-filters__section--active':
              openSection === 'color',
          },
        ]"
      >
        <button
          type="button"
          class="c-products-search-filters__section-header"
          @click="toggleSection('color')"
        >
          <h3 class="c-products-search-filters__filter-title">カラー</h3>
          <p>
            <span
              v-if="selectedFilterCounts.color > 0"
              class="c-products-search-filters__filter-option-selected-count"
            >
              {{ selectedFilterCounts.color }}件選択中</span
            >
            <span class="c-products-search-filters__section-icon">{{
              openSection === 'color' ? '−' : '+'
            }}</span>
          </p>
        </button>
        <transition
          name="accordion"
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div
            v-show="openSection === 'color'"
            class="c-products-search-filters__section-body"
          >
            <label
              class="c-products-search-filters__checkbox-wrapper color-on-checked"
              v-for="tag in aggregations.colorTags || []"
              :key="tag.value"
            >
              <input
                type="checkbox"
                v-model="searchParameters.tagsSet.colorTags"
                :value="tag.value"
              />
              <span class="c-products-search-filters__color">
                <div :data-color="tag.label"></div>
              </span>
              <p
                class="c-products-search-filters__filter-option-label"
                v-text="tag.label"
              ></p>
            </label>
          </div>
        </transition>
      </section>

      <section
        :class="[
          'c-products-search-filters__section',
          {
            'c-products-search-filters__section--active': openSection === 'tag',
          },
        ]"
      >
        <button
          type="button"
          class="c-products-search-filters__section-header"
          @click="toggleSection('tag')"
        >
          <h3 class="c-products-search-filters__filter-title">タグ</h3>
          <p>
            <span
              v-if="selectedFilterCounts.tag > 0"
              class="c-products-search-filters__filter-option-selected-count"
            >
              {{ selectedFilterCounts.tag }}件選択中</span
            >
            <span class="c-products-search-filters__section-icon">{{
              openSection === 'tag' ? '−' : '+'
            }}</span>
          </p>
        </button>
        <transition
          name="accordion"
          @before-enter="beforeEnter"
          @enter="enter"
          @after-enter="afterEnter"
          @before-leave="beforeLeave"
          @leave="leave"
          @after-leave="afterLeave"
        >
          <div
            v-show="openSection === 'tag'"
            class="c-products-search-filters__section-body"
          >
            <label
              class="c-products-search-filters__checkbox-wrapper color-on-checked"
              v-for="tag in aggregations.tasteTags || []"
              :key="tag.value"
            >
              <input
                type="checkbox"
                v-model="searchParameters.tagsSet.tasteTags"
                :value="tag.value"
              />
              <p
                class="c-products-search-filters__filter-option-label"
                v-text="tag.label"
              ></p>
            </label>
          </div>
        </transition>
      </section>
    </div>

    <div class="c-products-search-filters__buttons">
      <FButton @button-clicked="searchByFilters">検索</FButton>
      <FButton outline @button-clicked="reset">条件リセット</FButton>
    </div>
  </div>
</template>

<script>
import FButton from '~/js/components/button'

const SALE_PRODUCT_TYPES = ['廃番（家具）', '廃番（雑貨）']

export default {
  name: 'ComponentProductsSearchFilters',
  props: {
    searchParameters: {
      type: Object,
      required: true,
    },
    aggregations: {
      type: Object,
      required: true,
    },
    searchByFilters: {
      type: Function,
      required: true,
    },
    reset: {
      type: Function,
      required: true,
    },
    resetKeyword: {
      type: Function,
      required: true,
    },
  },
  components: {
    FButton,
  },
  data() {
    return {
      openSection: 'category',
      isComposingKeyword: false,
    }
  },
  methods: {
    onKeywordEnter(event) {
      if (
        this.isComposingKeyword ||
        event?.isComposing ||
        // keycode 229: キーボードの日本語入力（IME）の変換処理中であることを示す
        event?.keyCode === 229
      )
        return

      this.searchByFilters()
    },
    toggleSection(section) {
      this.openSection = this.openSection === section ? null : section
    },
    // アコーディオンアニメーション用
    beforeEnter(el) {
      el.style.maxHeight = '0'
      el.style.opacity = '0'
    },
    enter(el) {
      el.style.transition =
        'max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s'
      el.style.maxHeight = el.scrollHeight + 'px'
      el.style.opacity = '1'
    },
    afterEnter(el) {
      el.style.maxHeight = ''
      el.style.transition = ''
    },
    beforeLeave(el) {
      el.style.maxHeight = el.scrollHeight + 'px'
      el.style.opacity = '1'
    },
    leave(el) {
      el.style.transition =
        'max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s'
      void el.offsetHeight
      el.style.maxHeight = '0'
      el.style.opacity = '0'
    },
    afterLeave(el) {
      el.style.maxHeight = ''
      el.style.transition = ''
    },
  },
  computed: {
    containerClass() {
      return {
        'c-products-search-filters--open': this.openSection !== null,
        'c-products-search-filters--closed': this.openSection === null,
      }
    },
    hasSaleProductTypes() {
      return (this.aggregations.productTypes || []).some((type) =>
        SALE_PRODUCT_TYPES.includes(type.value)
      )
    },
    isSaleProductTypesChecked: {
      get() {
        const currentTypes = this.searchParameters.productTypes || []
        return SALE_PRODUCT_TYPES.every((type) => currentTypes.includes(type))
      },
      set(checked) {
        const currentTypes = this.searchParameters.productTypes || []

        if (checked) {
          this.searchParameters.productTypes = [
            ...new Set([...currentTypes, ...SALE_PRODUCT_TYPES]),
          ]
          return
        }

        this.searchParameters.productTypes = currentTypes.filter(
          (type) => !SALE_PRODUCT_TYPES.includes(type)
        )
      },
    },
    selectedFilterCounts() {
      const tagsSet = this.searchParameters?.tagsSet || {}

      return {
        category: Array.isArray(tagsSet.facetTags)
          ? tagsSet.facetTags.length
          : 0,
        tag: Array.isArray(tagsSet.tasteTags) ? tagsSet.tasteTags.length : 0,
        color: Array.isArray(tagsSet.colorTags) ? tagsSet.colorTags.length : 0,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '~/scss/generic/variables';
@use '~/scss/generic/mixins';
@use '~/fonts/icons/icons';

.c-products-search-filters {
  background-color: #fff;
  border: 1px solid rgba(variables.$color-brown, 0.12);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(variables.$color-text, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 16px;
  width: 100%;

  @include mixins.screen-desktop {
    width: 276px;
    height: calc(100% - 32px);
  }

  &__keyword-input-field {
    position: relative;
    flex-shrink: 0;

    input {
      background-color: #fff;
      border: 1px solid rgba(variables.$color-brown, 0.4);
      border-radius: 5px;
      font-size: 16px;
      padding: 10px 32px 10px 34px;
      width: 100%;

      &::placeholder {
        color: variables.$color-text-gray-2;
      }

      @include mixins.screen-desktop {
        font-size: 13px;
      }
    }
  }

  &__keyword-input-search {
    color: variables.$color-text-gray-2;
    font-size: 16px;
    left: 12px;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  &__keyword-input-clear {
    color: variables.$color-text-gray-2;
    cursor: pointer;
    font-size: 12px;
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
  }
  &__base-filters {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__checkbox-wrapper {
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: 8px;
    padding: 4px 8px;

    input[type='checkbox'] {
      appearance: none;
      -webkit-appearance: none;
      background-color: #fff;
      border: 1px solid variables.$color-brown;
      border-radius: 2px;
      cursor: pointer;
      height: 14px;
      width: 14px;
      position: relative;
      flex-shrink: 0;
    }

    input[type='checkbox']:checked::before {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -58%);
      color: #000;
      font-size: 18px;
      font-weight: bold;
    }

    &.color-on-checked {
      transition: background-color 0.2s ease;
      &:has(input[type='checkbox']:checked) {
        background-color: rgba(variables.$color-brown, 0.16);
      }
    }
  }

  &__filter-title {
    color: variables.$color-text;
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }

  &__filter-option-label {
    font-size: 12px;
    line-height: 1.45;
  }

  &__filter-option-selected-count {
    color: variables.$color-text-gray-2;
    padding-right: 8px;
    font-size: 11px;
  }

  &__price-range-inputs {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;

    input {
      background-color: #fff;
      border: 1px solid rgba(variables.$color-brown, 0.35);
      border-radius: 5px;
      width: 100%;
      padding: 6px 6px 6px 30px;
      font-size: 16px;
      &::placeholder {
        color: variables.$color-text-gray-2;
      }

      @include mixins.screen-desktop {
        font-size: 13px;
      }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__price-range-input {
    flex: 1;
    min-width: 0;
  }

  &__price-range-input-field {
    position: relative;
    width: 100%;
  }

  &__price-range-input-currency {
    position: absolute;
    top: 50%;
    left: 14px;

    transform: translateY(-50%);

    color: variables.$color-text-gray-2;
    font-size: 14px;

    pointer-events: none;
  }

  &__price-range-label {
    color: variables.$color-text-gray-2;
    font-size: 10px;
    font-weight: 600;
    margin-bottom: 4px;

    display: block;
  }

  &__price-range-separator {
    color: variables.$color-text-gray-2;
    font-size: 11px;
    margin-bottom: 10px;
    flex-shrink: 0;
  }

  &__filter-sale {
    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 12px;

    flex-shrink: 0;
  }

  hr {
    width: 100%;
    margin: 2px 0;
    border: 0;
    border-top: 1px solid rgba(variables.$color-brown, 0.12);
    flex-shrink: 0;
  }

  &__section {
    background-color: rgba(variables.$color-accent-weak, 0.35);
    border: 1px solid rgba(variables.$color-brown, 0.1);
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    min-height: fit-content;
    overflow: hidden;
  }

  &__sections {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    overflow: hidden;
  }

  // 開いている section のみ残り高さを使う
  &__section--active {
    flex: 0 1 auto;
    max-height: 100%;
    min-height: 0;
  }

  &__section-header {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 12px 14px;

    background: transparent;
    border: 0;

    text-align: left;

    cursor: pointer;
    user-select: none;

    transition: background-color 0.18s ease;

    flex-shrink: 0;

    &:hover {
      background-color: rgba(variables.$color-accent, 0.08);
    }
  }

  &__section-icon {
    color: rgba(variables.$color-accent, 0.72);

    font-size: 16px;
    font-weight: 500;
    line-height: 1;
  }

  &__section-body {
    flex: 0 1 auto;
    min-height: 0;

    overflow-y: auto;

    padding: 0 14px 10px;

    scrollbar-width: thin;
    scrollbar-color: rgba(variables.$color-brown, 0.24) transparent;

    box-shadow: inset 0 -12px 12px -12px rgba(variables.$color-brown, 0.24);

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(variables.$color-brown, 0.24);
      border-radius: 999px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(variables.$color-brown, 0.38);
    }
  }

  &__section--active &__section-body {
    flex: 1 1 auto;
  }

  &__buttons {
    display: flex;
    gap: 8px;

    margin-top: auto;
    padding-top: 12px;

    border-top: 1px solid rgba(variables.$color-brown, 0.12);

    flex-shrink: 0;

    > .c-button {
      flex: 1;
    }
  }

  &__color {
    width: 1rem;
    height: 1rem;

    margin-right: 4px;

    flex-shrink: 0;

    div {
      width: 100%;
      height: 100%;

      border-radius: 50%;
    }

    div[data-color='MULTI'] {
      background-image: url('//cdn.shopify.com/s/files/1/0132/7936/3136/t/3/assets/gradient_small.png?v=20854901883841102081561353776');
      background-position: center;
    }

    div[data-color='ホワイト'] {
      background-color: #fff;
      border: 1px solid #cbcbcb;
    }

    div[data-color='ブラック'] {
      background-color: #000;
    }

    div[data-color='グレー'] {
      background-color: #a3a3a3;
    }

    div[data-color='ブラウン'] {
      background-color: #5c3716;
    }

    div[data-color='ベージュ'] {
      background-color: #d1b999;
    }

    div[data-color='グリーン'] {
      background-color: #2c9748;
    }

    div[data-color='ブルー'] {
      background-color: #3150ae;
    }

    div[data-color='パープル'] {
      background-color: #954ec2;
    }

    div[data-color='イエロー'] {
      background-color: #f3e247;
    }

    div[data-color='ピンク'] {
      background-color: #ff87aa;
    }

    div[data-color='レッド'] {
      background-color: #f00;
    }

    div[data-color='オレンジ'] {
      background-color: #fb9026;
    }

    div[data-color='シルバー'] {
      background-image: url('//cdn.shopify.com/s/files/1/0132/7936/3136/t/3/assets/grey_small.png?v=169877580566453581991564582984');
      background-position: center;
    }

    div[data-color='ゴールド'] {
      background-image: url('//cdn.shopify.com/s/files/1/0132/7936/3136/t/3/assets/golden_small.png?v=38510460978240915571561353789');
      background-position: center;
    }
  }
}
.accordion-enter-active,
.accordion-leave-active {
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 400px;
  opacity: 1;
}
</style>
